import * as R from 'ramda';
import * as P from 'plow-js';
// components
import { getConfirmModal } from '../../components/confirm';
// helpers/constants
import * as G from '../../helpers';
import * as GC from '../../constants';
import { asyncGetTelsByCarrierInvoiceNumber } from '../../helpers/api-async';
// icons
import * as I from '../../svgs';
// feature invoice
import { telInvoiceInitFields } from './settings/tel-fields';
import { cloInvoiceInitFields, serviceVendorInitFields } from './settings/fields-settings';
//////////////////////////////////////////////////

export const getGuidsFromStringOrSelectedList = (data, list) => (
  G.ifElse(
    R.is(String, data),
    () => R.of(Array, data),
    () => R.compose(
      R.map((entity) => entity.guid),
      R.filter((entity) => entity.selected),
    )(list),
  )()
);

export const getItemListSuccess = (state, data) => {
  const { itemList, pagination } = state;

  const indexAdditional = G.ifElse(
    R.isNil(itemList),
    0,
    R.length(R.values(itemList)),
  );

  const newItems = data.results.map((item, index) => R.mergeRight(
    item,
    {
      selected: false,
      index: R.add(index, indexAdditional),
    },
  ));

  const list = R.mergeRight(itemList, R.indexBy(R.prop('guid'), newItems));
  const newOffset = R.add(pagination.offset, pagination.limit);

  return P.$all(
    P.$set('itemList', list),
    P.$set('pageVisited', true),
    P.$set('pagination.limit', 10),
    P.$set('totalCount', data.totalCount),
    P.$set(
      'pagination.offset',
      G.ifElse(
        R.gt(data.totalCount, newOffset),
        newOffset,
        data.totalCount,
      ),
    ),
    state,
  );
};

export const selectItem = (state, id) => {
  const { itemList } = state;
  if (R.equals(id, 'all')) {
    const value = R.not(R.all(
      (item) => item.selected,
      R.values(itemList),
    ));
    return P.$set(
      'itemList',
      R.map(
        (item) => R.assoc(
          'selected',
          value,
          item,
        ),
        itemList,
      ),
      state,
    );
  }
  return P.$toggle(`itemList.${id}.selected`, state);
};

export const changeInvoicesSuccess = (state, { data, guids }) => (
  P.$override(
    'itemList',
    R.map(
      (invoice) => {
        const newParams = R.find(R.eqProps(GC.FIELD_GUID, invoice), data);

        const updatedInvoice = R.mergeRight(newParams, {
          [GC.FIELD_SELECTED]: false,
          [GC.GRC.STATUS_DISPLAYED_VALUE]: R.path([GC.FIELD_STATUS, GC.FIELD_DISPLAYED_VALUE], newParams),
        });

        return R.mergeRight(invoice, updatedInvoice);
      },
      R.pick(guids, P.$get('itemList', state)),
    ),
    state,
  )
);

export const readyForBillingSuccess = (state, { data, guids }) => {
  const invoicesList = R.pick(guids, P.$get('itemList', state));
  return P.$override(
    'itemList',
    R.map(
      (invoice) => {
        const newProps = R.find(R.eqProps(GC.FIELD_GUID, invoice), data);
        const newPropsWithStatus = R.assoc(
          GC.GRC.STATUS_DISPLAYED_VALUE,
          R.path([GC.FIELD_STATUS, GC.FIELD_DISPLAYED_VALUE], newProps),
          newProps,
        );
        const newPropsWithSystemStatus = R.assoc(
          GC.FIELD_SYSTEM_STATUS,
          R.path([GC.FIELD_SYSTEM_STATUS], newPropsWithStatus),
          newPropsWithStatus,
        );
        return R.mergeRight(invoice, newPropsWithSystemStatus);
      },
      invoicesList,
    ),
    state,
  );
};

export const getRenderModal = (
  component,
  title,
  id,
) => ({
  component,
  options: {
    title,
    width: '310px',
    height: 'auto',
    controlButtons: [
      {
        id,
        type: 'button',
        margin: '0 12px 0 0',
        name: G.getWindowLocale('actions:save', 'Save'),
      },
    ],
  },
});

export const getChangeInvoicesTitle = (invoiceGuid) => G.getWindowLocale(
  G.ifElse(G.isString(invoiceGuid), 'titles:edit-invoice', 'titles:change-invoices'),
  'Change Invoices',
);

export const setConfigOptionGuidToEntity = (initValues) => (
  G.mapDropdownsObjectInEntity(
    GC.FIELD_CONFIG_GUID,
    [GC.FIELD_INVOICE_STATUS],
    G.mapDropdownsObjectInEntity(
      GC.FIELD_DROPDOWN_OPTION_GUID,
      [GC.FIELD_MODE, GC.FIELD_GL_CODE, GC.FIELD_SERVICE_TYPE],
      initValues,
    ),
  )
);

export const setConfigOptionGuidToEntity2 = (initValues) => (
  G.mapDropdownsObjectInEntity(
    GC.FIELD_CONFIG_GUID,
    [GC.FIELD_INVOICE_STATUS],
    G.mapDropdownsObjectInEntity(
      GC.FIELD_DROPDOWN_OPTION_GUID,
      [GC.FIELD_GL_CODE, GC.FIELD_SERVICE_TYPE],
      initValues,
    ),
  )
);

export const setDefaultInvoiceValues = (props, isTelInvoice) => {
  const {
    totalWeight,
    selectedRate,
    initialValues,
    totalDistance,
    defaultNetDays,
    defaultUomFields,
  } = props;

  let initFields = G.ifElse(isTelInvoice, telInvoiceInitFields, cloInvoiceInitFields);

  if (G.isNotNilAndNotEmpty(defaultNetDays)) {
    initFields = R.assoc(GC.FIELD_INVOICE_NET_DAYS, defaultNetDays, telInvoiceInitFields);
  }

  if (G.isNotNilAndNotEmpty(selectedRate)) {
    const fieldsToOmit = G.ifElse(
      isTelInvoice,
      [GC.FIELD_INVOICE_STATUS],
      [],
    );

    const data = R.mergeRight(initFields, setConfigOptionGuidToEntity(selectedRate));

    return R.omit(fieldsToOmit, data);
  }

  if (G.isNotNilAndNotEmpty(initialValues)) {
    const valuesToUse = G.ifElse(
      isTelInvoice,
      initialValues,
      R.assoc(
        GC.FIELD_GL_POSTED_DATE,
        R.pathOr(null, [GC.SYSTEM_OBJECT_ACCOUNTING_INTEGRATION, GC.FIELD_GL_POSTED_DATE], initialValues),
        initialValues,
      ),
    );

    const data = R.mergeRight(initFields, setConfigOptionGuidToEntity(valuesToUse));

    return data;
  }

  return G.setDefaultFieldsToRateInvoiceFormik({
    totalWeight,
    totalDistance,
    defaultUomFields,
    formikInitFields: initFields,
  });
};

export const setDefaultInvoiceValues2 = (props) => {
  const { asyncConfigs, totalWeight, totalDistance, initialValues } = props;

  if (G.isNotNilAndNotEmpty(initialValues)) {
    const data = R.mergeRight(serviceVendorInitFields, setConfigOptionGuidToEntity2(initialValues));

    return data;
  }

  const defaultUomFields = G.getDefaultUomFields(asyncConfigs);
  const defaultCurrency = G.getConfigValueFromStore(GC.GENERAL_BRANCH_DEFAULT_CURRENCY, asyncConfigs);

  return G.setDefaultFieldsToRateInvoiceFormik2({
    totalWeight,
    totalDistance,
    defaultCurrency,
    defaultUomFields,
    formikInitFields: serviceVendorInitFields,
  });
};

export const getTotalsFromValues = (values) => (
  R.pick(GC.GROUPED_FIELDS.TRIP_TOTALS_ARR, values)
);

const getInvoiceStatusOptions = (statusConfigName, props) => ({
  status: G.mapCustomConfigOptionsFromProps(statusConfigName, props, true),
});

export const setOptionsForSelect = (statusConfigName, props) => {
  const statusOptions = getInvoiceStatusOptions(statusConfigName, props);
  const configOptions = G.getModeServiceGLCodeFromAsyncConfigsForRateSelect(props);

  return R.mergeRight(statusOptions, configOptions);
};

export const setOptionsForServiceVendorSelect = (statusConfigName, props) => {
  const statusOptions = getInvoiceStatusOptions(statusConfigName, props);
  const configOptions = G.getModeGLCodeFromAsyncConfigsForRateSelect(props);
  const serviceType = R.pathOr([], ['vendorServiceTypeOptions'], props);

  return R.assoc('serviceType', serviceType, R.mergeRight(statusOptions, configOptions));
};

export const getArrowIcon = (expanded) => G.ifElse(
  expanded,
  I.arrowUpSimple,
  I.arrowDownSimple,
)(G.getTheme('colors.light.mainLight'));

export const isValidCharge = (event) => {
  const validExp = /^\d*(\.?\d*)$/;
  const decimal = R.pathOr('', ['1'], event.target.value.split('.'));
  const hasDecimal = G.ifElse(
    decimal.length,
    R.gte(2, decimal.length),
    true,
  );
  return R.and(validExp.test(event.target.value), hasDecimal);
};

const transformReferencesValue = (references, availableTypes) => R.map(
  (reference) => {
    const referenceTypeGuid = G.getPropFromObject(GC.FIELD_REFERENCE_TYPE_GUID, reference);
    const referenceType = R.find(R.propEq(GC.FIELD_GUID, referenceTypeGuid), availableTypes);
    const allowedValues = R.pathOr([], [GC.FIELD_REFERENCE_ALLOWED_VALUES], referenceType);

    if (R.and(G.isNotNilAndNotEmpty(allowedValues), G.notContain(G.getValueFromObject(reference), allowedValues))) {
      return R.assoc(GC.FIELD_VALUE, '', reference);
    }

    return reference;
  },
  references,
);

export const getDataWithTransformedReferences = (data, availableTypes) => {
  const references = R.pathOr([], [GC.FIELD_REFERENCES], data);

  if (G.isNilOrEmpty(references)) return data;

  const availableReferenceTypesGuids = R.pluck(GC.FIELD_GUID, availableTypes);

  const referencesToUse = transformReferencesValue(
    R.filter(
      ({ referenceTypeGuid }) => R.includes(referenceTypeGuid, availableReferenceTypesGuids),
      references,
    ),
    availableTypes,
    );

  return R.assoc(GC.FIELD_REFERENCES, referencesToUse, data);
};

export const invoiceNetDaysCustomBlurHandler = (event, field, props) => {
  const { values, handleBlur, setFieldValue } = props;

  const { payments, invoiceDate } = values;

  const value = G.getEventTargetValue(event);

  if (G.isAllTrue(
    G.isNilOrEmpty(payments),
    G.isNotNilAndNotEmpty(value),
    G.isValidDate(invoiceDate),
  )) {
    const numberValue = G.toNumber(value);

    const newPaymentDueDate = G.addDateTimeWithFormat(invoiceDate, numberValue, 'days', GC.DEFAULT_DATE_FORMAT);

    setFieldValue(GC.FIELD_INVOICE_PAYMENT_DUE_DATE, newPaymentDueDate);
  }

  handleBlur(event);
};

export const invoiceNetDaysCustomChangeHandler = (event, field, props) => {
  const { values, handleChange, setFieldValue } = props;

  const { payments, invoiceDate } = values;

  const value = G.getEventTargetValue(event);

  if (G.isAllTrue(
    G.isNilOrEmpty(payments),
    G.isNotNilAndNotEmpty(value),
    G.isValidDate(invoiceDate),
  )) {
    const numberValue = G.toNumber(value);

    const newPaymentDueDate = G.addDateTimeWithFormat(invoiceDate, numberValue, 'days', GC.DEFAULT_DATE_FORMAT);

    setFieldValue(GC.FIELD_INVOICE_PAYMENT_DUE_DATE, newPaymentDueDate);
  }

  handleChange(event);
};

export const invoiceDateCustomBlurHandler = (event, field, props) => {
  const { values, handleBlur, setFieldValue } = props;

  const { netDays, payments } = values;

  const value = G.getEventTargetValue(event);

  if (G.isAllTrue(
    G.isValidDate(value),
    G.isNilOrEmpty(payments),
    G.isNotNilAndNotEmpty(netDays),
  )) {
    const numberNetDaysValue = G.toNumber(netDays);

    const newPaymentDueDate = G.addDateTimeWithFormat(
      value,
      numberNetDaysValue,
      'days',
      GC.DEFAULT_DATE_FORMAT,
    );

    setFieldValue(GC.FIELD_INVOICE_PAYMENT_DUE_DATE, newPaymentDueDate);
  }

  handleBlur(event);
};

export const invoiceDateCustomChangeHandler2 = ({ value, props }) => {
  const { values, setFieldValue } = props;

  const { netDays, payments } = values;

  if (G.isAllTrue(
    G.isValidDate(value),
    G.isNilOrEmpty(payments),
    G.isNotNilAndNotEmpty(netDays),
  )) {
    const numberNetDaysValue = G.toNumber(netDays);

    const newPaymentDueDate = G.addDateTimeWithFormat(
      value,
      numberNetDaysValue,
      'days',
      GC.DEFAULT_DATE_FORMAT,
    );

    setFieldValue(GC.FIELD_INVOICE_PAYMENT_DUE_DATE, newPaymentDueDate);
  }

  setFieldValue(GC.FIELD_INVOICE_DATE, value);
};

const openCarrierInvoiceDuplicateConfirm = ({
  text,
  openModal,
  closeModal,
  submitAction,
}) => {
  const modalContent = getConfirmModal({
    text,
    cancelAction: closeModal,
    cancelText: G.getWindowLocale('actions:cancel', 'Cancel'),
    submitText: G.getWindowLocale('actions:confirm', 'Confirm'),
    submitAction: () => {
      closeModal();
      submitAction();
    },
  });

  openModal(modalContent);
};

const getCarrierInvoiceDuplicateConfirmText = (invoiceNumber, tripNumbers) => G.getWindowLocale(
  'messages:carrier-already-has-invoice-number',
  `This carrier already has invoice #${invoiceNumber} on trip ${tripNumbers}. Do you want to continue?`,
);

export const submitCarrierInvoiceWithDuplicateCheck = async ({
  payload,
  openModal,
  closeModal,
  invoiceGuid,
  submitAction,
}) => {
  const invoiceNumber = R.prop(GC.FIELD_INVOICE_NUMBER, payload);
  const carrierGuid = R.prop(GC.FIELD_CARRIER_GUID, payload);

  if (G.isNilOrEmpty(invoiceNumber)) return submitAction(payload);

  const requestParams = {
    invoiceGuid,
    carrierGuid,
    invoiceNumber,
  };

  const telPrimaryReferences = await asyncGetTelsByCarrierInvoiceNumber(requestParams);

  if (R.isNil(telPrimaryReferences)) return;

  if (G.isNilOrEmpty(telPrimaryReferences)) {
    return submitAction(payload);
  }

  openCarrierInvoiceDuplicateConfirm({
    openModal,
    closeModal,
    submitAction: () => submitAction(payload),
    text: getCarrierInvoiceDuplicateConfirmText(invoiceNumber, R.join(', ', telPrimaryReferences)),
  });
};

export const submitCarrierInvoicesWithDuplicateCheck = async ({
  invoices,
  openModal,
  closeModal,
  submitAction,
}) => {
  const results = await Promise.all(R.map(
    (invoice) => asyncGetTelsByCarrierInvoiceNumber({
      invoiceGuid: R.prop(GC.FIELD_GUID, invoice),
      carrierGuid: R.prop(GC.FIELD_CARRIER_GUID, invoice),
      invoiceNumber: R.prop(GC.FIELD_INVOICE_NUMBER, invoice),
    }),
    invoices,
  ));

  const tripNumbers = R.uniq(R.unnest(R.reject(R.isNil, results)));

  if (G.isNilOrEmpty(tripNumbers)) {
    return submitAction(invoices);
  }

  const invoiceNumber = R.compose(
    R.join(', '),
    R.uniq,
    R.reject(G.isNilOrEmpty),
    R.map(R.prop(GC.FIELD_INVOICE_NUMBER)),
  )(invoices);

  openCarrierInvoiceDuplicateConfirm({
    openModal,
    closeModal,
    submitAction: () => submitAction(invoices),
    text: getCarrierInvoiceDuplicateConfirmText(invoiceNumber, R.join(', ', tripNumbers)),
  });
};

export const getIsOwnCompanyCarrier = (entity) => R.pathOr(
  false,
  [GC.SYSTEM_OBJECT_CARRIER_ASSIGNMENT, GC.SYSTEM_OBJECT_CARRIER_SNAPSHOT, GC.FIELD_CARRIER_OWN_COMPANY],
  entity,
);

const ownCompanyCarrierInvoiceConfigNames = {
  [GC.INVOICE_TEL_CARRIER_INVOICE_DEFAULT_STATUS]: GC.INVOICE_TEL_OWN_COMPANY_CARRIER_INVOICE_DEFAULT_STATUS,
  [GC.INVOICE_TEL_CARRIER_INVOICE_NUMBER_SEQUENCE]: GC.INVOICE_TEL_OWN_COMPANY_CARRIER_INVOICE_NUMBER_SEQUENCE,
  [GC.INVOICE_TEL_CARRIER_INVOICE_DEFAULT_NET_DAYS]: GC.INVOICE_TEL_OWN_COMPANY_CARRIER_INVOICE_DEFAULT_NET_DAYS,
  [GC.INVOICE_TEL_SECOND_CARRIER_INVOICE_DEFAULT_STATUS]:
    GC.INVOICE_TEL_SECOND_OWN_COMPANY_CARRIER_INVOICE_DEFAULT_STATUS,
  [GC.INVOICE_TEL_CARRIER_INVOICE_DEFAULT_ATTACHMENT_DOCUMENT_TYPES]:
    GC.INVOICE_TEL_OWN_COMPANY_CARRIER_INVOICE_DEFAULT_ATTACHMENT_DOCUMENT_TYPES,
};

export const getCarrierInvoiceConfigValue = (carrierConfigName, isOwnCompany, getConfigValue) => {
  const carrierValue = getConfigValue(carrierConfigName);

  if (R.not(isOwnCompany)) return carrierValue;

  const ownCompanyValue = getConfigValue(R.prop(carrierConfigName, ownCompanyCarrierInvoiceConfigNames));

  if (G.isNilOrEmpty(ownCompanyValue)) return carrierValue;

  return ownCompanyValue;
};

export const getCarrierInvoiceDefaultsFromConfigs = (configs, isOwnCompany) => {
  const getConfigValue = (configName) => G.getConfigValueFromStore(configName, configs);

  return {
    defaultStatus: getCarrierInvoiceConfigValue(
      GC.INVOICE_TEL_CARRIER_INVOICE_DEFAULT_STATUS,
      isOwnCompany,
      getConfigValue,
    ),
    defaultNetDays: getCarrierInvoiceConfigValue(
      GC.INVOICE_TEL_CARRIER_INVOICE_DEFAULT_NET_DAYS,
      isOwnCompany,
      getConfigValue,
    ),
    defaultSecondInvoiceStatus: getCarrierInvoiceConfigValue(
      GC.INVOICE_TEL_SECOND_CARRIER_INVOICE_DEFAULT_STATUS,
      isOwnCompany,
      getConfigValue,
    ),
  };
};
